import React, { useState, useCallback, useEffect, useMemo, useRef } from 'react';
import styles from './CustomizeCollections.module.scss';
import { SelectOption } from '../../types';
import Spinner from 'src/components/spinner/Spinner';
import Button from 'src/components/button/Button';
import {
  useSllGetLessonsForSaLazyQuery,
  SllLesson,
  AusCourseContent,
  useGetAllCollectionsQuery,
  Collection,
  MultiCollectionContentType,
} from '../../api/graphql-codegen';
import { DndProvider } from 'react-dnd';
import { HTML5Backend } from 'react-dnd-html5-backend';
import IconPencil from 'src/assets/icons/pencil.svg?react';
import CoverLesson from '../CoverLesson/CoverLesson';
import { useToast } from "@src/hooks/useToast";
import CollectionFilterLayout from './FilterLayout';
import { Collapse } from 'react-collapse';
import MultiCollectionModal from '@src/pages/super-admin/multi-collections/modal/MultiCollectionModal';
import { unitOptions } from '@src/constants';
import MultiCollectionLayout from './dndLessonLayout/MultiCollectionLayout';
import CoverCollection from '../CoverCollection/CoverCollection';
import { useHistory } from 'react-router-dom';
import { debounce } from '@src/utils/helpers';
import { logger } from '../../utils/logger';

const unitMoveMessage = 'Content is moved successfully!';

type Props = {
  collection?: any;
  loadingCollection?: boolean;
  isDisabled?: boolean;
  handleSave?: (lessons: any[]) => void;
  handleExit?: (lessons: any[]) => void;
}

type lessonQuery = {
  keyword: string
  sortBy: 'name' | 'country'
  order: 'ASC' | 'DESC'
  page?: number
  perPage?: number
  ageGroup?: string
  isPublished: number
}

const defaultQuery: lessonQuery = {
  keyword: '',
  sortBy: 'name' as 'name' | 'country',
  order: 'ASC' as 'ASC' | 'DESC',
  page: 1,
  perPage: 24,
  ageGroup: '',
  isPublished: 1
}

const searchQuery = {
  keyword: '',
  sortBy: 'name' as 'name' | 'country',
  order: 'ASC' as 'ASC' | 'DESC',
}

function CustomizeMultiCollections(props: Props) {
  const { collection, isDisabled, loadingCollection, handleSave, handleExit } = props;

  const [keyword, setKeyword] = useState('');
  const [country, setCountry] = useState<string>('All');
  const [ageGroup, setAgeGroup] = useState<string>('');
  const [unitType, setUnitType] = useState<string>(''); // Lesson/Collection
  const [collectionType, setCollectionType] = useState<string>('');
  const [selCollection, setSelCollection] = useState(true);
  const [isMovable, setIsMovable] = useState(false);  // New Cover movable
  const [collectionLessons, setCollectionLessons] = useState<AusCourseContent[]>([]);
  const [editModal, setEditModal] = useState(false);

  const { successToast } = useToast();
  const history = useHistory();

  const [sllGetLessons, {loading, data}] = useSllGetLessonsForSaLazyQuery({fetchPolicy: 'network-only'});

  const debouncedApiCall = useRef(
    debounce((query: lessonQuery) => sllGetLessons({variables: {input: query}}), 500)
  ).current;

  const {data: collectionData, loading: allCollectionLoading} = useGetAllCollectionsQuery({
    fetchPolicy: 'network-only',
    variables: {
      input: {
        country: '',
        keyword: '',
        programId: ''
      }
    }
  })

  useEffect(() => {
    document.getElementById("root")!.style.overflow = "hidden"
  }, []);

  useEffect(() => {
    if (collection?.content) {
      setCollectionLessons(collection?.content ?? [])
    }
  }, [collection?.content])

  useEffect(() => {
    debouncedApiCall(defaultQuery)
  }, [debouncedApiCall])

  const changedCollection = useMemo(() => {
    if (collectionLessons === collection?.content) return true;
    else return false;
  }, [collection?.content, collectionLessons])

  const allLessons = useMemo(() => {
    return data?.sllGetLessonsForSA?.data ?? []
  }, [data?.sllGetLessonsForSA?.data])

  const allCollections = useMemo(() => {
    return collectionData?.getAllCollections ?? []
  }, [collectionData?.getAllCollections])

  const handleChangeSearch = useCallback((value) => {
    setKeyword(value);
    const newQuery: lessonQuery = {
      ...searchQuery,
      keyword: value,
      ageGroup: ageGroup,
      isPublished: 1
    }
    debouncedApiCall(newQuery)
  }, [ageGroup, debouncedApiCall]);

  const handleResetFilters = useCallback(() => {
    setUnitType('');
    setCollectionType('');
    setCountry('');
    setKeyword('');
    setAgeGroup('');
    debouncedApiCall(defaultQuery)
  }, [debouncedApiCall]);

  const handleChangeAgeGroup = useCallback((value: any) => {
    setAgeGroup(value);
    const newQuery: lessonQuery = {
      ...searchQuery,
      keyword: keyword,
      ageGroup: value,
      isPublished: 1
    }
    debouncedApiCall(newQuery)
  },[keyword, debouncedApiCall])

  const handleChangeCountry = useCallback((option: SelectOption) => {
    setCountry(option.value);
  },[])

  const handleChangeUnitType = (option: SelectOption) => {
    setUnitType(option.value);
  };

  const handleChangeCollectionType = (option: SelectOption) => {
    setCollectionType(option.value);
  };

  const filteredLessons = useMemo(() => {
    var filterLesson = data?.sllGetLessonsForSA?.data ?? []

    if (unitType === unitOptions[1].value) {
      filterLesson = [];
    }

    if (country && country !== 'All') {
      filterLesson = filterLesson.filter(lesson => (lesson?.country || '').includes(country));
    }

    if (ageGroup) {
      filterLesson = filterLesson.filter(lesson => lesson?.ageGroup && lesson?.ageGroup.includes(ageGroup))
    }

    return filterLesson;
  }, [country, unitType, data?.sllGetLessonsForSA?.data, ageGroup])

  const filteredCollections = useMemo(() => {
    var filterCollection = collectionData?.getAllCollections ?? []
    if (keyword) {
      filterCollection = filterCollection.filter(col => col?.name.toLowerCase().includes(keyword.toLowerCase()))
    }

    if (unitType === unitOptions[0].value) {
      filterCollection = [];
    }
    if (collectionType) {
      filterCollection = filterCollection.filter(col => col?.collectionType === collectionType)
    }
    if (country && country !== 'All') {
      filterCollection = filterCollection.filter(col => (col?.country || '').includes(country));
    }

    if (ageGroup) {
      filterCollection = filterCollection.filter(col => col?.grade && col?.grade.includes(ageGroup))
    }
    
    return filterCollection;
  }, [keyword, country, unitType, collectionType, collectionData?.getAllCollections, ageGroup])

  // Remove Grade Lesson
  const handleRemoveAusGradeContent = (content: AusCourseContent) => {
    setCollectionLessons((prev: any) => {
      const preLessons = prev as AusCourseContent[];
      const temp = preLessons.filter(item => item !== content);
      return temp;
    })
  }
  // handle Drag and Drop on Right Side
  const handleDnDOnLessonList = useCallback((index: number, lessons: string[]) => {
    const sortedArray = [...collectionLessons].sort((a, b) => {
      return lessons.indexOf(a.id) - lessons.indexOf(b.id);
    });
    setCollectionLessons(sortedArray);
  }, [collectionLessons]);
  // handle Drag and Drop
  const handleDrop = useCallback(
    (index: number, item: any) => {
      try {
        if (item.collection) {  // Drop with Collection
          const dropObj = Object.values(item) && Object.values(item)[0] as Collection
          const droppedCollection = allCollections.find(col => col?.id === dropObj.id)
          if (index === 0) {  // Remove Collection
            if (collectionLessons.find(item => item.id === droppedCollection?.id)) {
              const temp = [...collectionLessons];
              setCollectionLessons(temp.filter(u => u.id !== droppedCollection?.id));
            }
          } else if (index === 1) {  // Add Collection to Grade Content
            setCollectionLessons((prev: any) => {
              const preLessons = prev as any[];
              const exist = preLessons.find(item => item.id === droppedCollection?.id)
              if (exist) {
                return [...prev];
              } else {
                const addingContent: any = {
                  id: droppedCollection?.id ?? "",
                  bannerImage: droppedCollection?.bannerImage ?? "",
                  bannerVideo: droppedCollection?.bannerVideo ?? "",
                  name: droppedCollection?.name ?? "",
                  number: 0,
                  progress: 0,
                  subHeading: droppedCollection?.subHeading ?? "",
                  suggestedDuration: "",
                  suggestedLessonCount: droppedCollection?.sllLessons?.length ?? 0,
                  collectionType: droppedCollection?.collectionType,
                  type: MultiCollectionContentType.COLLECTION,
                }
                return [...prev, addingContent];
              }
            })
          }
          successToast(unitMoveMessage);
        } else if (item.lesson) { // Drop with Standalone Lesson

          const dropObj = Object.values(item) && Object.values(item)[0] as SllLesson
          const droppedLesson = allLessons.find(lesson => lesson?.id === dropObj.id)
          
          if (index === 0) {  // Remove lesson
            if (collectionLessons.find(item => item.id === droppedLesson?.id)) {
              const temp = [...collectionLessons];
              setCollectionLessons(temp.filter(u => u.id !== droppedLesson?.id));
            }
          } else if (index === 1) {  // Add Lesson to Main Content
            setCollectionLessons((prev: any) => {
              const preLessons = prev as AusCourseContent[];
              const exist = preLessons.find(item => item.id === droppedLesson?.id)
              if (exist) {
                return [...prev];
              } else {
                const addingContent: any = {
                  id: droppedLesson?.id ?? "",
                  categories: droppedLesson?.categories ?? "",
                  coverImage: droppedLesson?.coverImage ?? "",
                  name: droppedLesson?.name ?? "",
                  number: 0,
                  progress: 0,
                  subHeading: "",
                  suggestedDuration: droppedLesson?.suggestedDuration ?? "",
                  suggestedLessonCount: 0,
                  isNewCover: droppedLesson?.isNewCover ?? false,
                  type: MultiCollectionContentType.LESSON,
                }
                return [...prev, addingContent];
              }
            })
          }
          successToast(unitMoveMessage);
        }
      } catch (e) {
        logger.info(e);
      }
    }, [collectionLessons, allLessons, allCollections, successToast]
  );

  const handleMovableNewCover = (isNew: boolean) => {
    setIsMovable(isNew)
  }

  const handleEditCollectionName = () => {
    setEditModal(true)
  }

  const handleSelectCollection = () => {
    setSelCollection(current => !current)
  }

  const onEditCollection = (id: string) => {
    history.push(`/super/collections/edit/${id}`);
  }

  const collectionAndLessonContent = (
    <div>
      {loading ? <div className={styles.spinner}><Spinner /></div> : 
      <>
      <div className={styles.moreTitle}>Please input the lesson name for special Lessons.</div>
      <div className={styles.unitsDiv}>
      {filteredCollections.map((collection: any, index: number) => {
        return (
          <CoverCollection 
            key={index}
            collection={collection}
            type={"type"}
            source={"origin"}
            onEditCollection={onEditCollection}
            />
        )
      })}
      </div>
      <div className={styles.unitsDiv}>
      {filteredLessons.map((lesson, index) => {
        return (
          <CoverLesson
            key={index}
            lesson={lesson as SllLesson}
            type={"type"}
            source={"origin"}
            handleMovableNewCover={handleMovableNewCover}
          />
        )
      })}
      </div>
      </>}
    </div>
  )

  return (
    <DndProvider backend={HTML5Backend}>
      {(loadingCollection || allCollectionLoading) ? <div className={styles.spinner}><Spinner /></div> : <>
      <div id="customunits" className={styles.page}>
        <div className={styles.leftView}>
          <CollectionFilterLayout
            keyword={keyword}
            unitType={unitType}
            collectionType={collectionType}
            country={country}
            ageGroup={ageGroup}
            gradeSort={false}
            isMultiCollection={true}
            handleChangeAgeGroup={handleChangeAgeGroup}
            handleChangeUnitType={handleChangeUnitType}
            handleChangeCollectionType={handleChangeCollectionType}
            handleChangeSearch={handleChangeSearch}
            handleChangeCountry={handleChangeCountry}
            handleResetFilters={handleResetFilters}
          />
          {collectionAndLessonContent}
        </div>
        <div className={styles.rightView}>
          <div className={styles.actionsBanner}>
            <div className={styles.actionsBanner_buttons}>
              <Button className={styles.actionsBanner_cancel} onClick={() => handleExit && handleExit(collectionLessons)} size={'small'} label={'Exit'} />
              <Button 
                disabled={isDisabled || changedCollection}
                leftIcon={isDisabled ? <Spinner width={20} height={20} /> : undefined} className={styles.actionsBanner_done} 
                onClick={() => handleSave && handleSave(collectionLessons)} size={'small'} label={'Save'} />
            </div>
          </div>
          <div className={styles.lessonList}>
            <div className={styles.planItemContainer}>
              <div className={styles.planItem}>
                <div className={styles.subItem}>
                  <div className={styles.subItemTitle} >{('ABC').substring(0, 1).toLocaleUpperCase()}</div>
                </div>
                <div className={styles.planName} style={{color: `white`}}>
                  {collection?.name ?? ""}<br/>
                  {'Collection'}
                </div>
                <div className={styles.editPlan} onClick={handleEditCollectionName}>
                  <IconPencil fill={`white`} />
                </div>
                <div className={[styles.courseToggle, selCollection ? styles.courseToggleClose : styles.courseToggleOpen].join(' ')} onClick={handleSelectCollection}></div>
              </div>
            </div>
            <Collapse isOpened={selCollection}>
              <div className={styles.collectionLessons}>
                <MultiCollectionLayout
                  source={1}
                  contents={collectionLessons}
                  isNewMovable={isMovable}
                  handleDrop={handleDrop}
                  handleRemoveContentAction={handleRemoveAusGradeContent}
                  handleDnDOnLessonList={handleDnDOnLessonList}
                />
              </div>
            </Collapse>
          </div>
        </div>
      </div>
      {editModal && <MultiCollectionModal open={editModal} collection={collection} onClose={()=>setEditModal(false)}/>}
      </>}
    </DndProvider>
  );
}

export default CustomizeMultiCollections;
